import React, { useState, useEffect } from 'react';

const PercentageInput = React.forwardRef(({
  value = 0,
  onChange,
  placeholder = '',
  className = '',
  min = 0,
  max = 100,
  disabled = false,
  readOnly = false,
  showZero = false,
  decimalPlaces = 2,
  ...props
}, ref) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isUserTyping, setIsUserTyping] = useState(false);

  // Update display value when value prop changes (only if not user typing)
  useEffect(() => {
    if (!isUserTyping) {
      if (value === 0 && !showZero) {
        setDisplayValue('');
      } else {
        const formattedValue = Number(value).toFixed(decimalPlaces);
        setDisplayValue(formattedValue);
      }
    }
  }, [value, showZero, decimalPlaces, isUserTyping]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setIsUserTyping(true);
    
    // Allow empty string
    if (inputValue === '') {
      setDisplayValue('');
      onChange && onChange(0);
      return;
    }

    // Allow numbers with optional decimal places
    const cleanValue = inputValue.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      return; // Don't update if multiple decimal points
    }
    
    // Limit decimal places
    if (parts[1] && parts[1].length > decimalPlaces) {
      return; // Don't update if too many decimal places
    }

    if (cleanValue === '' || cleanValue === '.') {
      setDisplayValue(cleanValue);
      onChange && onChange(0);
      return;
    }

    const numValue = parseFloat(cleanValue);
    
    // Check if it's a valid number
    if (isNaN(numValue)) {
      return;
    }
    
    // Check min/max constraints
    if (numValue < min) {
      setDisplayValue(min.toString());
      onChange && onChange(min);
      return;
    }
    
    if (numValue > max) {
      setDisplayValue(max.toString());
      onChange && onChange(max);
      return;
    }

    setDisplayValue(cleanValue);
    onChange && onChange(numValue);
  };

  const handleBlur = () => {
    setIsUserTyping(false);
    
    // If empty on blur, set to 0
    if (displayValue === '' || displayValue === '.') {
      setDisplayValue(showZero ? '0.00' : '');
      onChange && onChange(0);
    } else {
      // Round to specified decimal places on blur
      const numValue = parseFloat(displayValue);
      if (!isNaN(numValue)) {
        const roundedValue = Math.round(numValue * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
        const formattedValue = roundedValue.toFixed(decimalPlaces);
        setDisplayValue(formattedValue);
        onChange && onChange(roundedValue);
      }
    }
  };

  const handleFocus = () => {
    setIsUserTyping(true);
    
    // If showing 0 and not showZero, clear the field on focus
    if ((displayValue === '0' || displayValue === '0.00') && !showZero) {
      setDisplayValue('');
    }
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      className={`${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${readOnly ? 'bg-gray-50' : ''}`}
      {...props}
    />
  );
});

export default PercentageInput;
